import { useId } from 'react';

export type TablePaginationProps = {
  /** Zero-based current page index (display shows `page + 1`). */
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  previousPageAriaLabel: string;
  nextPageAriaLabel: string;
  /** Omit footer when totalPages ≤ 1 (VSME dashboard pattern). Default false keeps hub tables showing 1/1. */
  hideWhenSinglePage?: boolean;
};

const ArrowNavIcon = ({ flipped }: { flipped?: boolean }) => {
  const rawId = useId().replace(/:/g, '_');
  const clipId = `${rawId}-clip`;

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={flipped ? { transform: 'scaleX(-1)' } : undefined}
      aria-hidden
      focusable="false"
    >
      <rect x="0.5" y="0.5" width="23" height="23" stroke="#C13D0C" />
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M14.8315 16.5204L14.8176 16.5353M14.8176 7.43262L19.2049 11.8199L16.3825 14.8677"
          stroke="#C13D0C"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4.77954 11.9834H4.73511M18.6792 11.9834H6.70602"
          stroke="#C13D0C"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="16" height="16" fill="white" transform="translate(4 4)" />
        </clipPath>
      </defs>
    </svg>
  );
};

/**
 * Matches hub/thn footer pagination; callers supply translated aria labels because text domains differ per plugin.
 */
const TablePagination = ({
  page,
  totalPages,
  onPrev,
  onNext,
  previousPageAriaLabel,
  nextPageAriaLabel,
  hideWhenSinglePage = false,
}: TablePaginationProps) => {
  if (hideWhenSinglePage && totalPages <= 1) {
    return null;
  }

  return (
    <footer className="thn-table-footer">
      <nav className="thn-table-footer__nav" style={{ marginRight: 'auto' }}>
        <button
          type="button"
          onClick={onPrev}
          disabled={page <= 0}
          aria-label={previousPageAriaLabel}
        >
          <ArrowNavIcon flipped />
        </button>
        <span className="thn-table-footer__page">
          {page + 1}/{totalPages}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages - 1}
          aria-label={nextPageAriaLabel}
        >
          <ArrowNavIcon />
        </button>
      </nav>
    </footer>
  );
};

export default TablePagination;
